<script setup lang="ts">
const app = useNuxtApp()
const route = useRoute()
const storeIndex = useStoreIndex()
const storeArticleList = useStoreArticleList()

definePageMeta({
  layout: 'layout-side-default',
  validate({ params }) {
    const app = useNuxtApp()
    const tabs = ['museum', 'event', 'report', 'special', 'news']
    if (tabs.includes(params.tab as string) && app.$anyToComma(params.id)) {
      return true
    }
    return false
  },
})

const tabItems = {
  museum: {
    label: 'ミュージアム',
    articleName: 'museum',
    listName: 'tagMuseum',
    component: 'MuseumItem',
  },
  event: {
    label: '展覧会・イベント',
    articleName: 'event',
    listName: 'tagEvent',
    component: 'EventItem',
  },
  report: {
    label: 'レポート',
    articleName: 'report',
    listName: 'tagReport',
    component: 'ReportItem',
  },
  special: {
    label: '特集',
    articleName: 'special',
    listName: 'tagSpecial',
    component: 'SpecialItem',
  },
  news: {
    label: 'ニュース',
    articleName: 'news',
    listName: 'tagNews',
    component: 'NewsItem',
  },
}

const refData = reactive({
  modelSort: '',
  tagItem: null as ArticleTag | null,
  tabItem: tabItems[route.params.tab as string],
  tabItemCount: {
    museum: null,
    event: null,
    report: null,
    special: null,
    news: null,
  },
})

const pageData = {
  items: [] as Article[],
  meta: {} as ApiMeta,
  sortItems: [],
}

let headTitle = ''
const setHead = () => {
  headTitle = `#${refData.tagItem.name}： ${refData.tabItem.label}一覧`

  // ぱんくず作成
  storeIndex.setBreadCrumbs([
    {
      text: 'タグ検索',
      disabled: true,
    },
  ])

  const headInput = app.$makeMeta({ title: headTitle })
  useHead(headInput)
}

const onChangeSort = (val) => {
  refData.modelSort = val
  const query = { ...route.query }
  if (val) {
    query.sort = val
  } else {
    delete query.sort
  }
  navigateTo({
    path: route.path,
    query,
  })
}

const initPage = async () => {
  const sortInfo = storeArticleList.getListSortInfo(refData.tabItem.listName)
  pageData.sortItems = sortInfo?.items || []
  refData.modelSort = sortInfo?.defaultValue || ''
  if (route.query.sort) {
    refData.modelSort = route.query.sort as string
  }

  const tagId = app.$anyToComma(route.params.id as string)

  const [_resSite, _resMaster, resTag, resList] = await Promise.all([
    storeIndex.fetchSite(),
    storeIndex.fetchMaster(),
    storeArticleList.fetchTagDetail(tagId),
    storeArticleList.fetchList(refData.tabItem.listName, { query: { tag: tagId } }),
  ])
  const tagDetail = resTag.data
  if (tagDetail.is_official !== 1) {
    throw createError({ statusCode: 940 })
    return
  } else {
    refData.tagItem = tagDetail
  }

  pageData.items = resList.data.articles as Article[]
  pageData.meta = resList.meta
  refData.tabItemCount[refData.tabItem.articleName] = pageData.meta.count
}
await initPage()
setHead()

onMounted(() => {
  // 他のタブの件数取得
  const query = { tag: refData.tagItem.id }
  const aFetchArticleName = []
  const aTabArticleName = []
  for (const key in tabItems) {
    if (key !== refData.tabItem.articleName) {
      aTabArticleName.push(key)
      aFetchArticleName.push(tabItems[key].listName)
    }
  }
  if (aFetchArticleName.length === 4) {
    Promise.all([
      storeArticleList.fetchList(aFetchArticleName[0], { query: query }),
      storeArticleList.fetchList(aFetchArticleName[1], { query: query }),
      storeArticleList.fetchList(aFetchArticleName[2], { query: query }),
      storeArticleList.fetchList(aFetchArticleName[3], { query: query }),
    ]).then((sRes) => {
      for (let i = 0; i < 4; i++) {
        refData.tabItemCount[aTabArticleName[i]] = sRes[i].meta.count
      }
    })
  }
})
</script>

<template>
  <div class="p-articlePage">
    <h2 class="c-heading_page">
      {{ headTitle }}
    </h2>
    <div class="l-pageDescription">
      <span
        class="l-pageDescription_text"
        v-html="refData.tagItem.description"
      />
    </div>

    <div class="c-category">
      <ul class="c-category_list">
        <li
          v-for="tab in tabItems"
          v-bind:key="tab.articleName"
          v-bind:class="{ active: refData.tabItem.articleName === tab.articleName }"
        >
          <nuxt-link
            v-bind:to="{ name: 'tag-id-tab', params: { id: refData.tagItem.id, tab: tab.articleName } }"
          >
            {{ tab.label }}
            <v-progress-circular
              v-show="refData.tabItemCount[tab.articleName] === null"
              indeterminate
              color="primary"
              size="16"
              width="2"
            />
            <span
              v-if="refData.tabItemCount[tab.articleName] !== null"
            >
              （{{ refData.tabItemCount[tab.articleName] }}）
            </span>
          </nuxt-link>
        </li>
      </ul>
    </div>

    <div class="p-articlePage_result">
      <div
        class="c-listHeader"
      >
        <div class="c-listHeader_col">
          <div class="c-listHeader_col_contentSearch">
            全 {{ app.$anyToComma(pageData.meta.count) }}件
          </div>
        </div>
        <div
          v-if="refData.tabItem.articleName !== 'news'"
          class="c-listHeader_dropDown"
        >
          <v-select
            v-model="refData.modelSort"
            class="IMCustomOverflowButton marginLeft c-searchSort"
            v-bind:items="pageData.sortItems"
            hide-details
            variant="outlined"
            density="comfortable"
            v-on:update:model-value="onChangeSort"
          />
        </div>
      </div>

      <div class="p-articlePage_list">
        <component
          v-bind:is="refData.tabItem.component"
          v-for="item in pageData.items"
          v-bind:key="item.id"
          v-bind:item="item"
        />
      </div>

      <Pagination
        v-bind:page="pageData.meta.page_no"
        v-bind:length="pageData.meta.total_page"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.l-pageDescription {
  margin-top: 27px;
  font-size: 16px;

  @include responsive.mq_down(sp) {
    &::after {
      content: "";
      display: block;
      clear: both;
    }
  }

  &_text {
    @include responsive.mq_down(sp) {
      display: block;
      margin-bottom: 10px;
    }
  }

  &_button {
    float: right;
    transition: var(--g_transition_button_hover);
    color: var(--color_primary_link);
    font-size: 14px;
    font-weight: bold;

    &_icon {
      color: var(--color_primary_link);
    }

    &:hover {
      transform: scale3d(1.1, 1.1, 1);
    }
  }
}

.c-category {
  margin-top: 50px;
  border-bottom: 1px solid var(--g_color_table_border_gray);

  @include responsive.mq_down(sp) {
    margin-bottom: 30px;
    padding-bottom: 20px;
  }

  &_list {
    display: flex;
    justify-content: space-between;
    padding-left: 0;

    @include responsive.mq_down(sp) {
      display: block;
    }

    li {
      position: relative;
      padding-bottom: 26px;
      transition: var(--g_transition_button_hover);
      color: var(--color_text_base);
      font-weight: bold;
      cursor: pointer;

      @include responsive.mq_down(sp) {
        padding: 8px 0;
      }

      a:hover {
        text-decoration: none;
      }

      &::after {
        content: "";
        display: inline-block;
        position: absolute;
        bottom: 0;
        left: 0;
        width: 0;
        height: 2px;
        transition: var(--g_transition_button_hover);
        background-color: transparent;
      }

      span {
        font-weight: bold;
      }

      &.active,
      &:hover {
        color: var(--color_primary_link);

        &::after {
          width: 100%;
          background-color: var(--color_primary_link);
        }
      }
    }
  }
}

.l-forYouLogin {
  margin-top: 62px;
  text-align: center;

  &_title {
    padding: 24px 0 7px;
    font-size: 20px;
    font-weight: bold;
  }
}
</style>
